import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { HeadingOne } from '@marty-js/design/src/atoms/h1';
import { useThemeSwitcher } from '@marty-js/design/src/utils/theme-switcher';
import classNames from 'classnames';
import { GenericItem, type TopicInfo } from '@marty-js/api-sdk/types';
import { HeadingTwo } from '@marty-js/design/src/atoms/h2';
import { SimpleItemProductCard } from '@marty-js/design/src/molecules/cards/product/simpleItem';
import { Slider } from '@marty-js/design/src/molecules/slider';
import { ToolkitItem } from '@marty-js/design/src/molecules/toolkit-item';
import { GoogleNewsSubscribe } from '@marty-js/design/src/atoms/google-news-subscribe';
import type { SponsoredType } from '@marty-js/design/src/atoms/types';
import { Inform } from '@marty-js/design/src/atoms/inform/inform';
import { useTopicQuery } from '@marty-js/api-sdk/services/sso';
import { Ad } from '../../../ads/ad';
import { Breadcrumb } from '../../../atoms/breadcrumb';
import { DateDisplay } from '../../../atoms/date/date';
import type { LayoutItemProps } from '../types';
import { Background, Container, FullContentRelative, MainContent, SideContent } from '../../../atoms/grid-container';
import { useSetMaxFirstImages } from '../../../utils/imageContext';
import { LoadLegacyCss } from '../../../atoms/loadLegacyCss';

import ItemSidebar from '../../../layout/sidebar/itemSidebar';
import type { ItemSidebarComponents } from '../../../layout/sidebar/types';
import { AuthorLine } from '../../../atoms/author-line';
import { AuthorDetailed } from '../../../atoms/author-detailed';
import { Comments } from '../../../atoms/Comments';
import { CommentsStatic } from '../../../atoms/CommentsStatic';

const LeftSide = styled.div`
  position: relative;
`;

const MainTitleItem = styled(HeadingOne)`
  color: var(--theme-color-title);
  grid-column: 2 / 3;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      grid-column: 1 / 10;

      &.mod-margin {
        margin: 0 0 30px;
      }
    `,
  )}
`;

export type LegacyData = {
  legacyArticle: {
    data: { html: string };
  };
  lastReviews: {
    genericItemList?: GenericItem[];
  };
  lastNews: {
    genericItemList?: GenericItem[];
  };
  comments: {
    commentsRaw: TopicInfo;
  };
} & ItemSidebarComponents;

const LegacyItem: React.FC<LayoutItemProps> = ({ item, data, breadcrumb, layout }: LayoutItemProps<LegacyData>) => {
  const t = useTranslation();
  useSetMaxFirstImages(3);

  const { topic, loading, fetchData, fetchMoreComments } = useTopicQuery();
  const [initialLoad, setInitialLoad] = useState(true);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (initialLoad) {
      fetchData({ variables: { id: item.id, userToken: localStorage.getItem('userToken') } });
    }
    setInitialLoad(false);
  }, [fetchData, initialLoad, item.id]);

  const loadMoreComments = () => {
    const newOffset = offset + 19;
    fetchMoreComments({
      id: item.id,
      userToken: localStorage.getItem('userToken'),
      offset: newOffset,
    });
    setOffset(newOffset);
  };

  const refSideBar = useRef<HTMLElement>();

  const hasAuthor = item.linkedWith?.author != null;
  const currentTheme = useThemeSwitcher();

  const lastReviews = useMemo(() => {
    const reviews = data?.lastReviews?.genericItemList;

    if (!reviews) {
      return null;
    }

    return (
      reviews.map((genericItem: GenericItem & { reviewUrl?: string }) => {
        const itemLink = {
          href: genericItem.url,
        };

        return (
          <SimpleItemProductCard
            key={genericItem.id}
            image={{ imageId: genericItem.imageId, alt: genericItem.title }}
            title={genericItem.title}
            nbComments={genericItem?.commentsNb}
            itemLink={itemLink}
            isSmall
            {...genericItem}
          />
        );
      }) ?? null
    );
  }, [data]);

  const lastNews = useMemo(() => {
    const news = data?.lastNews?.genericItemList;

    if (!news) {
      return null;
    }

    return (
      news.map((genericItem: GenericItem & { reviewUrl?: string }) => {
        const itemLink = {
          href: genericItem.url,
        };

        return (
          <SimpleItemProductCard
            key={genericItem.id}
            image={{ imageId: genericItem.imageId, alt: genericItem.title }}
            title={genericItem.title}
            itemLink={itemLink}
            isSmall
            {...genericItem}
          />
        );
      }) ?? null
    );
  }, [data]);

  const affiliationSentence =
    /Cet article contient des liens d.*?affiliation, ce qui signifie qu.*?une commission peut être reversée à Clubic/i;

  const sponsored: SponsoredType = {
    isSponsored: item.tagSlugs.includes('sponsored'),
    sponsors: item.sponsors || [],
  };

  return (
    <>
      <LoadLegacyCss />
      <Ad className="Billboard_1" desktopOnly megaban />
      <Container>
        <MainContent>
          <Breadcrumb breadcrumb={breadcrumb} isFullWidth />
          <MainTitleItem className={hasAuthor ? 'mod-margin' : ''}>{item.title}</MainTitleItem>
          <LeftSide>
            <AuthorLine
              authors={item.linkedWith?.author}
              nbComments={loading || initialLoad ? false : topic?.postsCount}
              sponsored={sponsored}
            >
              {item.updatedAt > item.publishedAt ? (
                <DateDisplay dateTime={item.updatedAt} pattern={t('localeTimePattern')} />
              ) : (
                <DateDisplay dateTime={item.publishedAt} pattern={t('localeTimePattern')} />
              )}
            </AuthorLine>
          </LeftSide>
          <div
            className={classNames({ 'article-content': true, 'dark-mode': currentTheme.isDarkTheme })}
            dangerouslySetInnerHTML={{ __html: data.legacyArticle?.data?.html }}
          />
          <AuthorDetailed authors={item.linkedWith?.author} />
          {layout === 'news' && <GoogleNewsSubscribe />}
          {!affiliationSentence.test(data.legacyArticle?.data?.html) && (
            <Inform
              affiliation={layout === 'deals' || item?.tagSlugs?.includes('review')}
              bestPick={layout === 'best-pick'}
              sponsored={sponsored}
              tags={item?.tagSlugs}
            />
          )}
          {!topic?.closed && (
            <Comments
              loadMoreComments={loadMoreComments}
              offset={0}
              topic={topic}
              fetchData={fetchData}
              itemId={item.id}
              loading={loading}
            />
          )}
          {data?.comments?.commentsRaw && data?.comments?.commentsRaw?.postStream?.posts?.length > 0 && (
            <CommentsStatic topic={data.comments.commentsRaw} />
          )}
        </MainContent>
        <SideContent ref={refSideBar}>
          <ItemSidebar refContainer={refSideBar} components={data} />
        </SideContent>
        {lastReviews && (layout === 'service-review' || layout === 'product-review' || layout === 'car-review') && (
          <FullContentRelative className="mod-spacer-top">
            {layout === 'service-review' && (
              <HeadingTwo className="mod-fat" as="div">
                {t('sdk.item.last_service_review')}
              </HeadingTwo>
            )}
            {layout === 'product-review' && (
              <HeadingTwo className="mod-fat" as="div">
                {t('sdk.item.last_review')}
              </HeadingTwo>
            )}
            {layout === 'car-review' && (
              <HeadingTwo className="mod-fat" as="div">
                {t('sdk.item.last_review')}
              </HeadingTwo>
            )}
            <Slider items={lastReviews} />
            <Background />
          </FullContentRelative>
        )}
        {lastNews && layout === 'news' && (
          <FullContentRelative className="mod-spacer-top">
            {layout === 'news' && (
              <HeadingTwo className="mod-fat" as="div">
                {t('sdk.item.last_news')}
              </HeadingTwo>
            )}
            <Slider items={lastNews} />
            <Background />
          </FullContentRelative>
        )}
      </Container>
      <ToolkitItem title={item.title} />
    </>
  );
};

export default LegacyItem;
